import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { APIResponse } from "src/app/core/models/api-response";
import { environment } from "src/environments/environment";
import { IPageCount } from "src/app/shared/models/page.model";
import { ICatalog, ICatalogFilterRequest, INewCatalogDTO, IHandlingUnitType } from "../models/catalogs.models";
import { IProduct } from "../../produits/models/products.models";
import { INewCatalog } from "../components/new-catalog/new-catalog.component";
import { ICustomer, ICompanyFilterRequest } from "../../customers/models/customers.models";

@Injectable({
  providedIn: 'root',
})
export class CatalogsService {
  private newCatalogDataSource = new BehaviorSubject<INewCatalog | null>(null);
  newCatalogData$: Observable<INewCatalog | null> = this.newCatalogDataSource.asObservable();

  constructor(private http: HttpClient) { }

  public listAllCatalogs(): Observable<APIResponse<ICatalog>> {
    return this.http.get<APIResponse<ICatalog>>(environment.BASE_URL_API + 'pricelists');
  }

  public listCatalogs(
    page: IPageCount,
    filter?: ICatalogFilterRequest | undefined,
    search?: string | undefined,
    sort_by?: string | undefined,
    sort_mode?: string | undefined
  ): Observable<APIResponse<ICatalog>> {
    const params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('filters', JSON.stringify(filter))
      .set('key', search!)
      .set('sort_by', sort_by!)
      .set('sort_mode', sort_mode!);
    return this.http.get<APIResponse<ICatalog>>(environment.BASE_URL_API + 'pricelists', { params })
  }

  public getCatalogById(id: number, simplified: boolean = false, includeCompanies: boolean = true): Observable<APIResponse<ICatalog>> {
    let params = new HttpParams();
    if (simplified) {
      params = params.set('simplified', 'true');
    }
    if (!includeCompanies) {
      params = params.set('include_companies', 'false');
    }
    // Cache-buster: the pricelist API response embeds the image as base64.
    // Without this, the browser caches the JSON response and the details view
    // shows the old image until a hard refresh. _t is ignored server-side.
    params = params.set('_t', Date.now().toString());
    return this.http.get<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'pricelists/' + id,
      { params }
    );
  }

  public GetCatalogProducts(
    id: number,
    page: number = 1,
    limit: number = 25,
    simplified: boolean = false,
    includeDeals: boolean = false
  ): Observable<APIResponse<IProduct>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('include_deals', includeDeals.toString());
    if (simplified) {
      params = params.set('simplified', 'true');
    }
    return this.http.get<APIResponse<IProduct>>(
      environment.BASE_URL_API + `pricelists/${id}/products`,
      { params }
    );
  }

  public addCatalog(catalog: Omit<ICatalog, 'id'>): Observable<APIResponse<ICatalog>> {
    return this.http.post<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'pricelists',
      catalog
    );
  }

  public updateCatalog(id: number, catalog: Omit<ICatalog, 'id'>): Observable<APIResponse<ICatalog>> {
    return this.http.patch<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'pricelists/' + id,
      catalog
    );
  }

  public deleteCatalog(id: number): Observable<APIResponse<ICatalog>> {
    return this.http.delete<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'pricelists/' + id,
    );
  }

  public deleteCatalogs(pricelist_ids: number[]): Observable<APIResponse<ICatalog>> {
    return this.http.delete<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'pricelists/bulk/delete',
      { body: { pricelist_ids: pricelist_ids } }
    );
  }

  public listAllCompanyCatalogsById(id: number): Observable<APIResponse<ICatalog>> {
    return this.http.get<APIResponse<ICatalog>>(environment.BASE_URL_API + 'locations/' + id + '/pricelists');
  }

  public getProductsByCompanyCatalogById(locationId: number, catalogId: number, orderId?: number, page?: number, limit?: number): Observable<APIResponse<IProduct>> {
    let params = new HttpParams();
    if (orderId) {
      params = params.set('order_id', orderId.toString());
    }
    if (page) {
      params = params.set('page', page.toString());
    }
    if (limit) {
      params = params.set('limit', limit.toString());
    }
    return this.http.get<APIResponse<IProduct>>(environment.BASE_URL_API + 'pricelists/' + catalogId + '/products', { params });
  }

  public shareCatalog(catalogId: number, data: { locations_ids: number[], attach_excel: boolean }): Observable<APIResponse<any>> {
    return this.http.post<APIResponse<any>>(
      `${environment.BASE_URL_API}pricelists/${catalogId}/share`,
      data
    );
  }

  public addAgentCatalog(catalog: Partial<INewCatalogDTO>): Observable<APIResponse<ICatalog>> {
    const payload = { ...catalog, type: 'agent' }
    return this.http.post<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'agent_pricelists',
      payload
    );
  }

  public updateAgentCatalog(id: number, catalog: Partial<INewCatalogDTO>): Observable<APIResponse<ICatalog>> {
    const payload = { ...catalog, type: 'agent' }
    return this.http.patch<APIResponse<ICatalog>>(
      environment.BASE_URL_API + 'agent_pricelists/' + id,
      payload
    )
  }

  public getNewCatalogData(): INewCatalog | null {
    return this.newCatalogDataSource.getValue();
  }

  private isNewDataEmpty(data: Partial<INewCatalog> | null): boolean {
    if (!data) return false;
  
    // Check if only subsidiary_id is present and equals 1
    const keys = Object.keys(data);
    if (keys.length === 1 && keys.includes('subsidiary_id') && data.subsidiary_id === 1) {
      return true;
    }
  
    // Check if all editable fields are empty
    return (
      data.cover === null &&
      data.description === '' &&
      data.expiration_date === '' &&
      data.name === ''
    );
  }
  

  public setNewCatalogData(data: Partial<INewCatalog> | null) {
    if (this.isNewDataEmpty(data)) return;
    const current = this.newCatalogDataSource.getValue();
    const newData = data ? { ...(current || {}), ...data } as INewCatalog : null;
    this.newCatalogDataSource.next(newData);
  }

  public exportCatalogs(filter: ICatalogFilterRequest, key: string): Observable<Blob> {
    const params = new HttpParams()
      .set('filters', JSON.stringify(filter))
      .set('key', key!);
    return this.http.get(environment.BASE_URL_API + 'pricelist/export', {
      responseType: 'blob', params
    });
  }

  public downloadCatalog(id: number): Observable<Blob> {
    return this.http.get(environment.BASE_URL_API + 'pricelists/' + id + '/download', {
      responseType: 'blob',
    });
  }

  public downloadCatalogs(
    page: IPageCount,
    filter?: ICatalogFilterRequest | undefined,
    search?: string | undefined,
    sort_by?: string | undefined,
    sort_mode?: string | undefined,
    pricelist_ids?: number[] | [] | undefined
  ): Observable<Blob> {

    const params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('filters', JSON.stringify(filter))
      .set('key', search!)
      .set('sort_by', sort_by!)
      .set('sort_mode', sort_mode!)
      .set('pricelist_ids', pricelist_ids?.join(',') || '');

    return this.http.get(environment.BASE_URL_API + 'pricelists/download', {
      params,
      responseType: 'blob',
    });
  }

  public getAvailableHandlingUnits(): Observable<APIResponse<IHandlingUnitType>> {
    return this.http.get<APIResponse<IHandlingUnitType>>(environment.BASE_URL_API + 'handling-unit-types');
  }

  public createHandlingUnitType(handlingUnitType: { name: string }): Observable<APIResponse<IHandlingUnitType>> {
    return this.http.post<APIResponse<IHandlingUnitType>>(
      environment.BASE_URL_API + 'handling-unit-type',
      handlingUnitType
    );
  }

  /**
   * Get paginated companies list for catalog audience selection
   * Uses the optimized /v1/companies/list endpoint (lightweight, fast)
   *
   * @param page - Page number (1-indexed)
   * @param limit - Items per page (default: 20, max: 100)
   * @param search - Search term for company name, ID, or segments
   * @param sortBy - Sort field (name, company_id, total_orders_count, last_activity_date)
   * @param sortMode - Sort direction (asc or desc)
   * @param pricelistId - Optional pricelist ID to mark assigned companies
   * @param filters - Optional filter parameters for advanced filtering
   * @returns Observable of paginated companies response
   */
  public getAudienceCompanies(
    page: number = 1,
    limit: number = 20,
    search?: string,
    sortBy: string = 'name',
    sortMode: 'asc' | 'desc' = 'asc',
    subsidiary_id?: number,
    pricelistId?: number,
    filters?: ICompanyFilterRequest
  ): Observable<APIResponse<ICustomer>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('sort_by', sortBy)
      .set('sort_mode', sortMode);

    if (search) {
      params = params.set('key', search);
    }

    if (subsidiary_id) {
      params = params.set('subsidiary_id', subsidiary_id.toString());
    }

    if (pricelistId) {
      params = params.set('pricelist_id', pricelistId.toString());
    }

    if (filters) {
      params = params.set('filters', JSON.stringify(filters));
    }

    return this.http.get<APIResponse<ICustomer>>(
      `${environment.BASE_URL_API}companies/list`,
      { params }
    );
  }
}